import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  getMedications,
  getPatientRecords,
  PATIENT_SOURCE_VENDOR,
  setConditionStatus,
  updateHealthConcern,
  type ConditionStatus,
  type ParsedRecord,
} from "@/features/patient/records/api/dataPipelineApi";
import {
  confirmNoteLink, confirmNoteLinks, getCareTimeline, getUploadCareSummary, linkNoteToVisit, rejectNoteLink, searchCareTimeline,
  type CareTimelineResponse,
} from "@/features/patient/care-treatment/api/careTimelineApi";
import { getRecentLabs } from "@/features/patient/dashboard/api/recentLabsApi";
import { nextPoll } from "@/features/patient/care-treatment/utils/careTimelinePolling";
import { getPatientProfileApi } from "@/portals/patient/api/patientProfileApi";
import { listCaregiversApi, listDependentsApi, listIncomingPatientRequestsApi } from "@/portals/patient/api/familyApi";
import { STALE, unwrap } from "@/lib/query/queryClient";
import {
  deleteRejectedUploadApi,
  getReviewItemApi,
  getReviewItemsCountApi,
  getReviewProcessingApi,
  listOtherNamesApi,
  listReviewItemsApi,
  removeOtherNameApi,
  submitReviewResponseApi,
  type ReviewAnswer,
  type ReviewItem,
  type ReviewProcessing,
} from "@/portals/patient/api/reviewItemsApi";

/** How often the processing banner re-checks while new records are being worked on. */
export const PROCESSING_POLL_MS = 3_000;
import type { CaregiverAccess, Dependent, IncomingPatientAccessRequest } from "@/shared/types/family";
import { getUploadStatus } from "@/features/patient/records/api/dataPipelineApi";

/**
 * Query keys for the patient portal. Everything clinical sits under the
 * viewed profile (`profileId` = the dependent being viewed, or "self"), so
 * switching profile can never show another person's data, and a profile's
 * whole cache can be invalidated at once.
 */
const scope = (profileId: string | undefined) => ["patient", "profile", profileId ?? "self"] as const;

export const patientKeys = {
  all: ["patient"] as const,
  account: ["patient", "account"] as const,
  family: ["patient", "family"] as const,
  profile: scope,
  records: (profileId: string | undefined) => [...scope(profileId), "records"] as const,
  /** Under records too: Care & Treatment's visits and notes refresh with them. */
  careTimeline: (profileId: string | undefined) => [...scope(profileId), "records", "care-timeline"] as const,
  /** The patient's visit answers this session, kept apart from the timeline so a refetch never drops them. */
  careTimelineAnswers: (profileId: string | undefined) => [...scope(profileId), "care-timeline-answers"] as const,
  /** Under records too: the dashboard's lab widget refreshes with them. */
  recentLabs: (profileId: string | undefined) => [...scope(profileId), "records", "recent-labs"] as const,
  medications: (profileId: string | undefined) => [...scope(profileId), "medications"] as const,
  /** Prefix for every review query (lists, items, count, processing). */
  review: (profileId: string | undefined) => [...scope(profileId), "review"] as const,
  uploads: (profileId: string | undefined) => [...scope(profileId), "uploads"] as const,
  imaging: (profileId: string | undefined) => [...scope(profileId), "imaging"] as const,
};

export const recordsQuery = (profileId: string | undefined) =>
  queryOptions({
    queryKey: patientKeys.records(profileId),
    queryFn: () => getPatientRecords({ actingAsProfileId: profileId }),
    staleTime: STALE.records,
    // The heaviest request in the app: not re-sent on every tab switch. Socket
    // events (and the resync after a reconnect) already refresh it the moment
    // anything changes.
    refetchOnWindowFocus: false,
  });

/** Care & Treatment's visits and notes. */
export const careTimelineQuery = (profileId: string | undefined) =>
  queryOptions({
    queryKey: patientKeys.careTimeline(profileId),
    queryFn: () => getCareTimeline(profileId),
    staleTime: STALE.records,
    // Scanned pages are read in the background: re-check (a cheap 304 when
    // nothing changed) at 10 s, 30 s, then every 60 s, for up to 15 minutes.
    refetchInterval: (query) => nextPoll(profileId ?? "", query.state.data),
  });

/** Care & Treatment search: the viewed profile's visits and notes matching q. */
export const careTimelineSearchQuery = (profileId: string | undefined, q: string) =>
  queryOptions({
    queryKey: [...patientKeys.careTimeline(profileId), "search", q] as const,
    queryFn: () => searchCareTimeline(q, profileId),
    enabled: q.trim().length > 0,
    staleTime: STALE.records,
  });

/** "What we found" for one upload (the upload result screen). */
export const uploadCareSummaryQuery = (profileId: string | undefined, uploadId: string) =>
  queryOptions({
    queryKey: [...patientKeys.careTimeline(profileId), "upload-summary", uploadId] as const,
    queryFn: () => getUploadCareSummary(uploadId, profileId),
    staleTime: STALE.records,
  });

/** The dashboard's "Most Recent Lab Result" widget, worked out by the backend. */
export const recentLabsQuery = (profileId: string | undefined) =>
  queryOptions({
    queryKey: patientKeys.recentLabs(profileId),
    queryFn: () => getRecentLabs(profileId),
    staleTime: STALE.records,
  });

/** Medications on their own: a far lighter request than the every-type records fetch. */
export const medicationsQuery = (profileId: string | undefined) =>
  queryOptions({
    queryKey: patientKeys.medications(profileId),
    queryFn: () => getMedications(profileId),
    staleTime: STALE.records,
  });

export const accountProfileQuery = () =>
  queryOptions({
    queryKey: patientKeys.account,
    queryFn: async () => {
      const { data, error } = await getPatientProfileApi();
      if (!data) throw new Error(error ?? "Couldn't load your profile.");
      return data;
    },
    staleTime: STALE.slow,
    // Over a slow tunnel this one call fails more often than on localhost;
    // without retries the header would sit on the placeholder name forever.
    retry: import.meta.env.MODE === "test" ? false : 2,
    retryDelay: 1000,
  });

export interface FamilyData {
  dependents: Dependent[];
  caregivers: CaregiverAccess[];
  incomingPatientRequests: IncomingPatientAccessRequest[];
}

// Family/caregiver module paused (2026-10-28): its backend endpoints aren't
// mounted, so nothing is fetched and every screen sees "no family". Set true
// to switch it back on (with the backend's _FAMILY_MODULE_ENABLED and routers).
const FAMILY_MODULE_ENABLED = false;

export const familyQuery = () =>
  queryOptions({
    queryKey: patientKeys.family,
    queryFn: async (): Promise<FamilyData> => {
      if (!FAMILY_MODULE_ENABLED) return { dependents: [], caregivers: [], incomingPatientRequests: [] };
      const [dependents, caregivers, incoming] = await Promise.all([
        listDependentsApi(),
        listCaregiversApi(),
        listIncomingPatientRequestsApi(),
      ]);
      const error = dependents.error ?? caregivers.error ?? incoming.error;
      if (error) throw new Error(error);
      return {
        dependents: dependents.data ?? [],
        caregivers: caregivers.data ?? [],
        incomingPatientRequests: incoming.data ?? [],
      };
    },
    // Three requests per fetch; socket events refresh it when it changes.
    staleTime: STALE.slow,
  });

/**
 * The records (every query under them) refetch. Care & Treatment's timeline is
 * slow to build on a big record, so a fetch of it already under way is never
 * thrown away and started again (each refresh used to restart it, and the
 * screen waited for the last one): it finishes and shows, then refetches once
 * (a cheap 304 when nothing changed).
 */
export async function invalidateRecords(client: QueryClient, profileId: string | undefined): Promise<void> {
  const timelineKey = patientKeys.careTimeline(profileId);
  const timeline = client.getQueryCache().find({ queryKey: timelineKey, exact: true });
  await client.invalidateQueries({
    queryKey: patientKeys.records(profileId),
    predicate: (query) => query !== timeline,
  });
  if (!timeline) return;
  if (timeline.state.fetchStatus === "fetching") await timeline.promise?.catch(() => undefined);
  await client.invalidateQueries({ queryKey: timelineKey, exact: true });
}

/**
 * After anything that changes a profile's clinical data (upload finished,
 * review answered or decided, sync done): records, medications and the
 * review queue refetch together — whichever of them are on screen.
 */
export function invalidatePatientData(client: QueryClient, profileId: string | undefined): Promise<void> {
  return Promise.all([
    invalidateRecords(client, profileId),
    client.invalidateQueries({ queryKey: patientKeys.medications(profileId) }),
    client.invalidateQueries({ queryKey: patientKeys.review(profileId) }),
  ]).then(() => undefined);
}

// ── Medication review (Human Review Queue, badge, review modal) ─────────────

const isBusy = (p?: ReviewProcessing | null) => Boolean(p && p.state !== "idle");

/** Every review item of the profile (open and resolved), newest first. */
export const reviewListQuery = (profileId: string | undefined) =>
  queryOptions({
    queryKey: [...patientKeys.review(profileId), "list"] as const,
    queryFn: async () => (await unwrap(listReviewItemsApi("all", profileId))).items,
    staleTime: STALE.live,
  });

/** Items awaiting the patient's input — the sidebar badge. */
export const reviewCountQuery = (profileId: string | undefined) =>
  queryOptions({
    queryKey: [...patientKeys.review(profileId), "count"] as const,
    queryFn: async () => Math.max(0, (await unwrap(getReviewItemsCountApi(profileId))).awaitingInput),
    staleTime: STALE.live,
  });

export const reviewItemQuery = (profileId: string | undefined, itemId: string) =>
  queryOptions({
    queryKey: [...patientKeys.review(profileId), "item", itemId] as const,
    queryFn: () => unwrap(getReviewItemApi(itemId, profileId)),
    staleTime: STALE.live,
  });

/**
 * New records still being read / matched / compared. Polled only while
 * something is running (every few seconds, slower after a failed check so a
 * hiccup never freezes the card), and not at all while idle or while the
 * tab is hidden.
 */
/**
 * Module 5's identity result for one upload (Records > upload > Issues). Under
 * the review keys, so answering an identity item or an identity notification
 * refreshes it; re-checked every few seconds only while the check is running.
 */
export const uploadIdentityQuery = (uploadId: string, profileId: string | undefined) =>
  queryOptions({
    queryKey: [...patientKeys.review(profileId), "upload-identity", uploadId] as const,
    queryFn: async () => (await getUploadStatus(uploadId, profileId)).identity_check ?? null,
    staleTime: STALE.live,
    refetchInterval: (query) => (query.state.data?.state === "checking" ? PROCESSING_POLL_MS : false),
  });

export const reviewProcessingQuery = (profileId: string | undefined) =>
  queryOptions({
    queryKey: [...patientKeys.review(profileId), "processing"] as const,
    queryFn: () => unwrap(getReviewProcessingApi(profileId)),
    staleTime: 0,
    refetchInterval: (query) =>
      isBusy(query.state.data) ? (query.state.status === "error" ? PROCESSING_POLL_MS * 2 : PROCESSING_POLL_MS) : false,
  });

export { isBusy as isReviewProcessingBusy };

/** Whether an item counts toward the "awaiting your input" badge. */
const awaitsPatient = (item: ReviewItem) => item.awaitingInput || Boolean(item.clinicianQuestion);

/** What an item looks like right after the patient answered it — shown before the server confirms. */
function optimisticallyAnswered(item: ReviewItem, answer: ReviewAnswer, comment?: string, chosenRecordId?: string): ReviewItem {
  const response = { answer, comment: comment ?? null, respondedAt: new Date().toISOString(), ...(chosenRecordId ? { chosenRecordId } : {}) };
  if (item.triggerType === "IDENTITY_MISMATCH") {
    // Patient-only: "this is / isn't me" settles it; anything else leaves it waiting on them.
    const settles = answer === "SAME_PERSON" || answer === "NOT_SAME_PERSON";
    return {
      ...item,
      itemStatus: settles ? "RESOLVED_PATIENT_REPORTED" : item.itemStatus,
      awaitingInput: !settles,
      canRespond: !settles,
      patientResponses: [...item.patientResponses, response],
    };
  }
  return {
    ...item,
    itemStatus: item.itemStatus === "AWAITING_PATIENT" ? "AWAITING_CLINICIAN" : item.itemStatus,
    awaitingInput: false,
    // Still open: more messages may follow (an open review stays answerable).
    canRespond: item.canRespond ?? item.awaitingInput,
    clinicianQuestion: null,
    patientResponses: [...item.patientResponses, response],
  };
}

export interface SubmitReviewVariables {
  itemId: string;
  answer: ReviewAnswer;
  comment?: string;
  /** USE_THIS_VALUE only: the option the patient picked. */
  chosenRecordId?: string;
}

/**
 * Sends the patient's answer / message / skip. The queue, the item and the
 * badge update instantly (optimistically) and roll back if the server
 * refuses; on success the server's copy replaces the guess, and records,
 * medications and the review queue revalidate.
 */
/**
 * Deletes an upload the patient said isn't them (hidden now, erased after 30
 * days). Records, the review queue and the uploads list refetch afterwards —
 * in the background: a returned promise would hold mutateAsync (the button's
 * spinner) until every one of them had reloaded.
 */
/** Identifies upload deletions in the mutation cache: the bulk-review screen
 *  collapses the other cards of an upload the patient just deleted. */
export const deleteUploadMutationKey = (profileId: string | undefined) => [...patientKeys.review(profileId), "delete-upload"] as const;

export function useDeleteRejectedUpload(profileId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationKey: deleteUploadMutationKey(profileId),
    mutationFn: (itemId: string) => unwrap(deleteRejectedUploadApi(itemId, profileId)),
    onSettled: () => {
      void invalidatePatientData(client, profileId);
      void client.invalidateQueries({ queryKey: patientKeys.uploads(profileId) });
    },
  });
}

/**
 * The patient marks a diagnosis / health concern active, inactive or resolved.
 * A hospital's record gets the patient's mark beside it (never changed); the
 * patient's own concern has its status changed directly — both show on the
 * Diagnoses page. The item moves to its new group at once (the cached records
 * are updated before the server answers, and rolled back if it refuses);
 * afterwards only the records reload, in the background.
 */
export function useSetConditionStatus(profileId: string | undefined) {
  const client = useQueryClient();
  const key = patientKeys.records(profileId);
  const isOwn = (id: string) =>
    client.getQueryData<ParsedRecord[]>(key)?.find((r) => r.id === id)?.sourceVendor === PATIENT_SOURCE_VENDOR;
  const mark = (records: ParsedRecord[] | undefined, id: string, status: ConditionStatus) => records?.map((r) => {
    if (r.id !== id) return r;
    if (r.sourceVendor === PATIENT_SOURCE_VENDOR) return { ...r, fields: { ...r.fields, clinical_status: status } };
    return { ...r, patientStatus: {
      status, setAt: new Date().toISOString(), hospitalNewer: false,
      hospitalStatus: r.patientStatus?.hospitalStatus ?? status,
    } };
  });
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ConditionStatus }) => (isOwn(id)
      ? { own: await updateHealthConcern(id, { status }, profileId) }
      : { mark: await setConditionStatus(id, status) }),
    onMutate: async ({ id, status }) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<ParsedRecord[]>(key);
      client.setQueryData<ParsedRecord[]>(key, (records) => mark(records, id, status));
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
    },
    onSuccess: (saved, { id }) => {
      client.setQueryData<ParsedRecord[]>(key, (records) => records?.map((r) => {
        if (r.id !== id) return r;
        return saved.own ? { ...r, ...saved.own } : { ...r, patientStatus: saved.mark };
      }));
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key });
    },
  });
}

/** The profile's other names. Under the review keys: an answer that adds one refreshes it. */
export const otherNamesQuery = (profileId: string | undefined) =>
  queryOptions({
    queryKey: [...patientKeys.review(profileId), "other-names"] as const,
    queryFn: async () => (await unwrap(listOtherNamesApi(profileId))) ?? [],
    staleTime: STALE.live,
  });

/** Removes one other name; the list and every review view refresh. */
export function useRemoveOtherName(profileId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (otherId: string) => unwrap(removeOtherNameApi(otherId, profileId)),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: patientKeys.review(profileId) });
    },
  });
}

export function useSubmitReviewResponse(profileId: string | undefined) {
  const client = useQueryClient();
  const listKey = reviewListQuery(profileId).queryKey;
  const countKey = reviewCountQuery(profileId).queryKey;
  return useMutation({
    mutationFn: ({ itemId, answer, comment, chosenRecordId }: SubmitReviewVariables) =>
      unwrap(submitReviewResponseApi(itemId, { answer, ...(comment ? { comment } : {}), ...(chosenRecordId ? { chosenRecordId } : {}) }, profileId)),
    onMutate: async ({ itemId, answer, comment, chosenRecordId }) => {
      const itemKey = reviewItemQuery(profileId, itemId).queryKey;
      await Promise.all([
        client.cancelQueries({ queryKey: listKey }),
        client.cancelQueries({ queryKey: countKey }),
        client.cancelQueries({ queryKey: itemKey }),
      ]);
      const previous = {
        list: client.getQueryData(listKey),
        count: client.getQueryData(countKey),
        item: client.getQueryData(itemKey),
      };
      const before = previous.item ?? previous.list?.find((i) => i.id === itemId);
      if (previous.list) {
        client.setQueryData(listKey, previous.list.map((i) => (i.id === itemId ? optimisticallyAnswered(i, answer, comment, chosenRecordId) : i)));
      }
      if (previous.item) client.setQueryData(itemKey, optimisticallyAnswered(previous.item, answer, comment, chosenRecordId));
      if (previous.count !== undefined && before && awaitsPatient(before) && !optimisticallyAnswered(before, answer, comment).awaitingInput) {
        client.setQueryData(countKey, Math.max(0, previous.count - 1));
      }
      return { previous, itemKey };
    },
    onError: (_error, _variables, context) => {
      if (!context) return;
      client.setQueryData(listKey, context.previous.list);
      client.setQueryData(countKey, context.previous.count);
      client.setQueryData(context.itemKey, context.previous.item);
    },
    onSuccess: (submitted, { itemId }) => {
      if (!submitted) return;
      client.setQueryData(reviewItemQuery(profileId, itemId).queryKey, submitted);
      // A "Yes" that settled other identity questions: they close at once on every
      // screen (only "Remove from my records" is left to do), before the lists reload.
      const settled = new Set(submitted.alsoConfirmedIds ?? []);
      const closeSettled = (i: ReviewItem): ReviewItem => (settled.has(i.id)
        ? { ...i, itemStatus: "RESOLVED_PATIENT_REPORTED", awaitingInput: false, canRespond: true, allowedAnswers: ["NOT_SAME_PERSON"] }
        : i);
      client.setQueryData(listKey, (list) => list?.map((i) => (i.id === itemId ? submitted : closeSettled(i))));
      settled.forEach((id) => client.setQueryData(reviewItemQuery(profileId, id).queryKey, (i) => (i ? closeSettled(i) : i)));
      if (settled.size) client.setQueryData(countKey, (n: number | undefined) => (n === undefined ? n : Math.max(0, n - settled.size)));
    },
    // Win or lose, the server is the source of truth afterwards. Not awaited:
    // the answer already shows (optimistic + the server's copy above), and a
    // returned promise would hold the button until the records reloaded.
    onSettled: () => {
      void invalidatePatientData(client, profileId);
    },
  });
}

/** noteId → the patient's answer this session: its suggested visit confirmed or not, or the visit they picked. */
export type NoteAnswer = "confirm" | "reject" | { visit: string };
export type NoteAnswers = Record<string, NoteAnswer>;

function rememberAnswers(client: QueryClient, profileId: string | undefined, noteIds: string[], answer: NoteAnswer) {
  client.setQueryData<NoteAnswers>(patientKeys.careTimelineAnswers(profileId), (current) => ({
    ...current,
    ...Object.fromEntries(noteIds.map((id) => [id, answer])),
  }));
}

function forgetAnswers(client: QueryClient, profileId: string | undefined, noteIds: string[]) {
  if (noteIds.length === 0) return;
  client.setQueryData<NoteAnswers>(patientKeys.careTimelineAnswers(profileId), (current) => {
    const next = { ...current };
    noteIds.forEach((id) => delete next[id]);
    return next;
  });
}

/**
 * The answers given this session. The first page of notes is updated in the
 * cache directly; notes from older pages live outside it, so Care & Treatment
 * applies these to them (otherwise a confirmed older note stays in To review).
 */
export function useNoteAnswers(profileId: string | undefined): NoteAnswers {
  const client = useQueryClient();
  const key = patientKeys.careTimelineAnswers(profileId);
  const { data } = useQuery({
    queryKey: key,
    // Never fetched: written by the mutations; a refetch just keeps what is there.
    queryFn: () => client.getQueryData<NoteAnswers>(key) ?? {},
    initialData: {},
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data;
}

/**
 * "Confirm visit" / "Keep unlinked" on a note with a suggested visit. The note
 * leaves "To review" at once (the cached timeline is updated before the server
 * answers, and put back if it refuses); the timeline then reloads.
 */
export function useAnswerNoteLink(profileId: string | undefined) {
  const client = useQueryClient();
  const key = patientKeys.careTimeline(profileId);
  return useMutation({
    mutationFn: ({ noteId, confirm }: { noteId: string; confirm: boolean }) =>
      (confirm ? confirmNoteLink : rejectNoteLink)(noteId, profileId),
    onMutate: async ({ noteId, confirm }) => {
      rememberAnswers(client, profileId, [noteId], confirm ? "confirm" : "reject");
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<CareTimelineResponse>(key);
      client.setQueryData<CareTimelineResponse>(key, (data) => data && {
        ...data,
        notes: data.notes.map((n) => n.id !== noteId ? n : {
          ...n,
          encounterId: confirm ? n.suggestedEncounterId ?? null : null,
          linkConfidence: confirm ? "high" : "low",
          suggestedEncounterId: undefined,
          keptUnlinked: confirm ? undefined : true,
        }),
      });
      return { previous };
    },
    onError: (_error, { noteId }, context) => {
      forgetAnswers(client, profileId, [noteId]);
      if (context?.previous) client.setQueryData(key, context.previous);
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key });
    },
  });
}

/**
 * A note with no visit: linked to the visit the patient picked. It leaves "To
 * review" at once (put back if the server refuses); the timeline then reloads.
 */
export function useLinkNoteToVisit(profileId: string | undefined) {
  const client = useQueryClient();
  const key = patientKeys.careTimeline(profileId);
  return useMutation({
    mutationFn: ({ noteId, encounterId }: { noteId: string; encounterId: string }) =>
      linkNoteToVisit(noteId, encounterId, profileId),
    onMutate: async ({ noteId, encounterId }) => {
      rememberAnswers(client, profileId, [noteId], { visit: encounterId });
      await client.cancelQueries({ queryKey: key, exact: true });
      const previous = client.getQueryData<CareTimelineResponse>(key);
      client.setQueryData<CareTimelineResponse>(key, (data) => data && {
        ...data,
        notes: data.notes.map((n) => n.id !== noteId ? n : {
          ...n, encounterId, linkConfidence: "high", suggestedEncounterId: undefined,
        }),
      });
      return { previous };
    },
    onError: (_error, { noteId }, context) => {
      forgetAnswers(client, profileId, [noteId]);
      if (context?.previous) client.setQueryData(key, context.previous);
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key });
    },
  });
}

/**
 * "Confirm all suggestions from this file": the notes leave "To review" at
 * once; any the server refused come back (the timeline reloads after).
 */
export function useConfirmNoteLinks(profileId: string | undefined) {
  const client = useQueryClient();
  const key = patientKeys.careTimeline(profileId);
  return useMutation({
    mutationFn: (noteIds: string[]) => confirmNoteLinks(noteIds, profileId),
    onMutate: async (noteIds) => {
      rememberAnswers(client, profileId, noteIds, "confirm");
      await client.cancelQueries({ queryKey: key, exact: true });
      const previous = client.getQueryData<CareTimelineResponse>(key);
      const ids = new Set(noteIds);
      client.setQueryData<CareTimelineResponse>(key, (data) => data && {
        ...data,
        notes: data.notes.map((n) => !ids.has(n.id) ? n : {
          ...n, encounterId: n.suggestedEncounterId ?? null, linkConfidence: "high", suggestedEncounterId: undefined,
        }),
      });
      return { previous };
    },
    onSuccess: (results) => {
      // Refused ones go back to To review.
      forgetAnswers(client, profileId, results.filter((r) => !r.ok).map((r) => r.noteId));
    },
    onError: (_error, noteIds, context) => {
      forgetAnswers(client, profileId, noteIds);
      if (context?.previous) client.setQueryData(key, context.previous);
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key });
    },
  });
}
